
import React, { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import AddInvestmentDialog from './AddInvestmentDialog';
import { usePortfolio, addInvestmentToPortfolio } from './portfolioUtils';
import { useParams, Link, useNavigate } from 'react-router-dom';
import './App.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const MULTI_TIMEFRAME_SPECS = [
  { timeframe: '1H', points: 48, title: '1H', subtitle: 'Impulsion courte' },
  { timeframe: '4H', points: 42, title: '4H', subtitle: 'Contexte swing' },
  { timeframe: '1D', points: 30, title: '1D', subtitle: 'Tendance large' },
];

function normalizeChartPayload(payload, fallbackTimeframe) {
  return {
    timeframe: payload?.timeframe || fallbackTimeframe,
    source: payload?.source || 'UNAVAILABLE',
    points: Array.isArray(payload?.points)
      ? payload.points.filter(point => typeof point?.value === 'number' && !Number.isNaN(point.value))
      : [],
  };
}

function formatChartTimestamp(timestamp, timeframe) {
  if (!timestamp) {
    return '';
  }

  const parsed = new Date(timestamp);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }

  switch (timeframe) {
    case '1H':
      return parsed.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    case '4H':
      return `${parsed.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })} ${parsed.toLocaleTimeString('fr-FR', { hour: '2-digit' })}`;
    case '1D':
    default:
      return parsed.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
  }
}

function resolveChartTone(points) {
  if (!Array.isArray(points) || points.length < 2) {
    return '#214f77';
  }

  return points[points.length - 1].value >= points[0].value ? '#1f8a4c' : '#c04a3b';
}

function formatChartSource(source) {
  switch (source) {
    case 'BINANCE_CANDLES':
      return 'Source Binance';
    case 'DAILY_HISTORY':
      return 'Historique journalier';
    case 'DAILY_HISTORY_FALLBACK':
      return 'Fallback journalier';
    default:
      return 'Série indisponible';
  }
}

function formatCompactPrice(value) {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return 'n/a';
  }

  return `$${value.toLocaleString('en-US', { minimumFractionDigits: value >= 100 ? 2 : 4, maximumFractionDigits: value >= 100 ? 2 : 6 })}`;
}

function TimeframeMiniChartCard({ spec, chart, loading }) {
  const points = Array.isArray(chart?.points) ? chart.points : [];
  const tone = resolveChartTone(points);
  const latestValue = points.length ? points[points.length - 1].value : null;

  return (
    <div
      style={{
        background: 'var(--color-surface)',
        borderRadius: 14,
        boxShadow: '0 10px 28px var(--color-card-shadow, #0001)',
        padding: 16,
        minHeight: 220,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#6a7980', fontWeight: 700 }}>
            {spec.title}
          </div>
          <div style={{ fontSize: 13, color: '#4f646d', marginTop: 4 }}>{spec.subtitle}</div>
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, color: tone, whiteSpace: 'nowrap' }}>
          {formatCompactPrice(latestValue)}
        </div>
      </div>

      {loading ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6a7980', fontWeight: 600 }}>
          Chargement…
        </div>
      ) : points.length > 1 ? (
        <div style={{ height: 132 }}>
          <Line
            data={{
              labels: points.map(point => formatChartTimestamp(point.timestamp, spec.timeframe)),
              datasets: [
                {
                  data: points.map(point => point.value),
                  borderColor: tone,
                  backgroundColor: `${tone}18`,
                  fill: true,
                  tension: 0.28,
                  pointRadius: 0,
                  borderWidth: 2,
                },
              ],
            }}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: { display: false },
                title: { display: false },
                tooltip: {
                  callbacks: {
                    label: (context) => formatCompactPrice(context.parsed.y),
                  },
                },
              },
              scales: {
                x: {
                  ticks: { maxTicksLimit: 4, color: '#6a7980' },
                  grid: { display: false },
                },
                y: {
                  ticks: {
                    maxTicksLimit: 4,
                    color: '#6a7980',
                    callback: (value) => formatCompactPrice(Number(value)),
                  },
                  grid: { color: 'rgba(33, 79, 119, 0.08)' },
                },
              },
            }}
          />
        </div>
      ) : (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6a7980', textAlign: 'center', lineHeight: 1.6 }}>
          Pas assez de données pour cette vue.
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: '#617178', fontWeight: 600 }}>{formatChartSource(chart?.source)}</span>
        <span style={{ fontSize: 12, color: '#89979c' }}>{points.length ? `${points.length} points` : 'Aucune série'}</span>
      </div>
    </div>
  );
}


export default function CryptoDetail() {
  const { id } = useParams();
  const [crypto, setCrypto] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [currency, setCurrency] = useState('');
  const [exchanges, setExchanges] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [priceHistory, setPriceHistory] = useState([]);
  const [chartViewMode, setChartViewMode] = useState('single');
  const [multiTimeframeCharts, setMultiTimeframeCharts] = useState({});
  const [multiTimeframeLoading, setMultiTimeframeLoading] = useState(false);
  const [multiTimeframeError, setMultiTimeframeError] = useState('');
  const navigate = useNavigate();
  const portfolio = usePortfolio();

  useEffect(() => {
    setChartViewMode('single');
    setMultiTimeframeCharts({});
    setMultiTimeframeError('');

    fetch('/api/cryptos')
      .then(res => res.json())
      .then(data => {
        const arr = Array.isArray(data.content) ? data.content : data;
        const found = arr.find(c => String(c.id) === String(id));
        setCrypto(found);
      });
    fetch('/api/currencies')
      .then(res => res.json())
      .then(data => setCurrencies(data));
    fetch('/api/exchanges?size=1000')
      .then(res => res.json())
      .then(data => {
        const arr = Array.isArray(data.content) ? data.content : data;
        setExchanges(arr.map(e => e.code || e.name || e.id).filter(Boolean));
      });
    const cookie = document.cookie.match(/currency=([^;]+)/);
    setCurrency(cookie ? cookie[1] : '');

    // Récupère l'historique des prix
    fetch(`/api/cryptos/${id}/history`)
      .then(res => res.json())
      .then(data => {
        // Génère 30 jours de labels
        const today = new Date();
        const days = Array.from({length: 30}, (_, i) => {
          const d = new Date(today.getTime() - (29-i)*24*60*60*1000);
          d.setHours(0,0,0,0);
          return d;
        });
        let history = Array.isArray(data) ? data : [];
        // Mappe chaque jour à son prix ou 0
        const filled = days.map(day => {
          const found = history.find(h => {
            const hDate = new Date(h.recordedAt);
            hDate.setHours(0,0,0,0);
            return hDate.getTime() === day.getTime();
          });
          return {
            recordedAt: day.toISOString(),
            price: found ? found.price : 0
          };
        });
        setPriceHistory(filled);
      });
  }, [id]);

  useEffect(() => {
    if (chartViewMode !== 'multi' || !id || Object.keys(multiTimeframeCharts).length === MULTI_TIMEFRAME_SPECS.length) {
      return undefined;
    }

    let cancelled = false;

    const loadMultiTimeframeCharts = async () => {
      setMultiTimeframeLoading(true);
      setMultiTimeframeError('');

      const results = await Promise.allSettled(
        MULTI_TIMEFRAME_SPECS.map(async (spec) => {
          const response = await fetch(`/api/cryptos/${id}/chart?timeframe=${encodeURIComponent(spec.timeframe)}&points=${spec.points}`);
          if (!response.ok) {
            throw new Error(`Vue ${spec.timeframe} indisponible`);
          }
          return [spec.timeframe, normalizeChartPayload(await response.json(), spec.timeframe)];
        })
      );

      if (cancelled) {
        return;
      }

      const nextCharts = {};
      const failures = [];

      results.forEach((result, index) => {
        const spec = MULTI_TIMEFRAME_SPECS[index];
        if (result.status === 'fulfilled') {
          const [timeframe, payload] = result.value;
          nextCharts[timeframe] = payload;
        } else {
          nextCharts[spec.timeframe] = normalizeChartPayload(null, spec.timeframe);
          failures.push(spec.timeframe);
        }
      });

      setMultiTimeframeCharts(nextCharts);
      if (failures.length === MULTI_TIMEFRAME_SPECS.length) {
        setMultiTimeframeError('La vue multi-timeframes est indisponible pour cet actif.');
      } else if (failures.length > 0) {
        setMultiTimeframeError(`Certaines vues n'ont pas pu être chargées : ${failures.join(', ')}.`);
      }
      setMultiTimeframeLoading(false);
    };

    loadMultiTimeframeCharts().catch(() => {
      if (!cancelled) {
        setMultiTimeframeLoading(false);
        setMultiTimeframeError('Impossible de charger la vue multi-timeframes.');
      }
    });

    return () => {
      cancelled = true;
    };
  }, [chartViewMode, id, multiTimeframeCharts]);

  const converted = (() => {
    if (
      currency &&
      currency.toUpperCase() !== 'USD' &&
      crypto &&
      crypto.currentPrice !== undefined &&
      crypto.currentPrice !== null &&
      currencies.length > 0
    ) {
      const cur = currencies.find(c => c.code === currency.toUpperCase());
      if (cur && cur.conversionRate) {
        const price = crypto.currentPrice * cur.conversionRate;
        return `≈ ${price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 8 })} ${cur.code}`;
      }
    }
    return null;
  })();

  if (!crypto) return <div style={{ padding: 32 }}>Crypto not found.</div>;

  return (
    <>
      <div className="crypto-detail-page">
        <div style={{ marginBottom: 16 }}>
          <Link to="/" className="nav-link">Accueil</Link> |
          <Link to="/cryptos" className="nav-link">Toutes les cryptos</Link>
        </div>
        <div style={{ width: '100vw', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', marginTop: 32 }}>
          <div className="crypto-card" style={{ width: '100%', maxWidth: '1200px', minHeight: 400, display: 'flex', flexDirection: 'row', boxSizing: 'border-box', padding: 0 }}>
            {/* Colonne gauche : infos */}
            <div style={{ flex: 1, minWidth: 0, padding: 32, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center' }}>
              <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                <img src={crypto.imgUrl} alt={crypto.name} className="crypto-img" style={{ maxWidth: 80, maxHeight: 80, display: 'block' }} />
              </div>
              <div className="crypto-info" style={{ width: '100%' }}>
                <div className="crypto-code">{crypto.code}</div>
                <div className="crypto-name">{crypto.name}</div>
                <div className="crypto-symbol">Symbole : {crypto.symbol}</div>
                <div className="crypto-marketcap">Market Cap : {(crypto.marketCap ?? crypto.marketcap) ? (crypto.marketCap ?? crypto.marketcap).toLocaleString('en-US') : 'N/A'}</div>
                <div className="crypto-marketcap-rank">Market Cap Rank : {crypto.marketCapRank ?? 'N/A'}</div>
                <div className="crypto-marketcap-category">
                  Catégorie : {(() => {
                    const cap = crypto.marketCap ?? crypto.marketcap;
                    if (cap < 10000000) return 'Nano Cap';
                    if (cap < 100000000) return 'Micro Cap';
                    if (cap < 1000000000) return 'Small Cap';
                    if (cap < 10000000000) return 'Mid Cap';
                    return 'Large Cap';
                  })()}
                </div>
                <div className="crypto-ath">ATH : {crypto.ath !== undefined && crypto.ath !== null ? `$${crypto.ath.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 8 })}` : 'N/A'}</div>
                <div className="crypto-atl">ATL : {crypto.atl !== undefined && crypto.atl !== null ? `$${crypto.atl.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 8 })}` : 'N/A'}</div>
                <div className="crypto-price" style={{ marginTop: 8, fontWeight: 500 }}>
                  {crypto.currentPrice !== undefined && crypto.currentPrice !== null
                    ? `Prix : $${crypto.currentPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 8 })}`
                    : <span style={{ color: '#aaa' }}>Prix inconnu</span>}
                  {converted && (
                    <div style={{ fontSize: 14, color: '#888', marginTop: 4 }}>{converted}</div>
                  )}
                </div>
              </div>
            </div>
            {/* Colonne droite : graphe + price change */}
            <div style={{ flex: 1, minWidth: 0, padding: 32, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start' }}>
              <div style={{ width: '100%', marginBottom: 16, display: 'flex', justifyContent: chartViewMode === 'single' ? 'center' : 'flex-start' }}>
                <div style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6, padding: 4, borderRadius: 999, background: '#edf2f4' }}>
                  <button
                    type="button"
                    onClick={() => setChartViewMode('single')}
                    style={{
                      border: 'none',
                      borderRadius: 999,
                      padding: '10px 16px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      background: chartViewMode === 'single' ? '#17323c' : 'transparent',
                      color: chartViewMode === 'single' ? '#f8f4ec' : '#17323c',
                    }}
                  >
                    Vue standard
                  </button>
                  <button
                    type="button"
                    onClick={() => setChartViewMode('multi')}
                    style={{
                      border: 'none',
                      borderRadius: 999,
                      padding: '10px 16px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      background: chartViewMode === 'multi' ? '#17323c' : 'transparent',
                      color: chartViewMode === 'multi' ? '#f8f4ec' : '#17323c',
                    }}
                  >
                    Vue 1H / 4H / 1D
                  </button>
                </div>
              </div>

              {chartViewMode === 'single' ? (
                priceHistory.length > 1 && (
                  <div style={{ width: '100%', maxWidth: 400, background: 'var(--color-surface)', borderRadius: 8, boxShadow: '0 2px 8px var(--color-card-shadow, #0001)', padding: 16, marginBottom: 24, color: 'var(--color-text-main)' }}>
                    <h3 style={{ textAlign: 'center', marginBottom: 16 }}>Historique du prix (30 jours)</h3>
                    <Line
                      data={{
                        labels: priceHistory.map(h => new Date(h.recordedAt).toLocaleDateString()),
                        datasets: [
                          {
                            label: 'Prix ($)',
                            data: priceHistory.map(h => h.price),
                            borderColor:
                              priceHistory.every(p => p.price === 0)
                                ? '#888'
                                : priceHistory[0].price < priceHistory[priceHistory.length - 1].price
                                  ? 'green'
                                  : 'red',
                            backgroundColor: 'rgba(0,0,0,0.05)',
                            fill: true,
                            tension: 0.2,
                            pointRadius: 2
                          }
                        ]
                      }}
                      options={{
                        responsive: true,
                        plugins: {
                          legend: { display: false },
                          title: { display: false }
                        },
                        scales: {
                          x: { display: true, title: { display: false }, ticks: { color: 'var(--color-text-main)' } },
                          y: { display: true, title: { display: false }, ticks: { color: 'var(--color-text-main)' } }
                        }
                      }}
                    />
                  </div>
                )
              ) : (
                <div style={{ width: '100%', marginBottom: 24 }}>
                  <div style={{ marginBottom: 12, color: '#4f646d', lineHeight: 1.6 }}>
                    Trois graphes simplifiés pour comparer la micro-impulsion, le contexte intermédiaire et la tendance large sans quitter le cockpit.
                  </div>
                  {multiTimeframeError && (
                    <div style={{ marginBottom: 12, color: '#a14b2d', fontWeight: 600 }}>{multiTimeframeError}</div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
                    {MULTI_TIMEFRAME_SPECS.map(spec => (
                      <TimeframeMiniChartCard
                        key={spec.timeframe}
                        spec={spec}
                        chart={multiTimeframeCharts[spec.timeframe]}
                        loading={multiTimeframeLoading && !multiTimeframeCharts[spec.timeframe]}
                      />
                    ))}
                  </div>
                </div>
              )}
              <div className="crypto-price-change" style={{ marginBottom: 8 }}>
                <span>Price Change 24h : </span>
                <span style={{ color: crypto.priceChange24h > 0 ? 'green' : crypto.priceChange24h < 0 ? 'red' : undefined }}>
                  {crypto.priceChange24h !== undefined && crypto.priceChange24h !== null ? `${crypto.priceChange24h > 0 ? '+' : ''}${crypto.priceChange24h.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 8 })}` : 'N/A'}
                </span>
              </div>
              <div className="crypto-price-change-percentage">
                <span>Price Change % 24h : </span>
                <span style={{ color: crypto.priceChangePercentage24h > 0 ? 'green' : crypto.priceChangePercentage24h < 0 ? 'red' : undefined }}>
                  {crypto.priceChangePercentage24h !== undefined && crypto.priceChangePercentage24h !== null ? `${crypto.priceChangePercentage24h > 0 ? '+' : ''}${crypto.priceChangePercentage24h.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}%` : 'N/A'}
                </span>
              </div>
            </div>
          </div>
        </div>
        {/* Boutons sous la card, centrés */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 32, width: '100%' }}>
          {portfolio && (
            <button className="nav-link" onClick={()=>setAddOpen(true)}>Ajouter au portefeuille</button>
          )}
          <button onClick={() => navigate(-1)} className="nav-link">Back</button>
        </div>
        <AddInvestmentDialog
          open={addOpen}
          onClose={()=>setAddOpen(false)}
          onAdd={inv => {
            // Ajout dans le portefeuille local
            addInvestmentToPortfolio(inv);
            setAddOpen(false);
          }}
          cryptoSymbol={crypto.code}
          exchanges={exchanges}
        />
      </div>
    </>
  );
}
